import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import SearchMenuListItem from '../SearchForm/SearchMenu/SearchMenuListItem'
import ExperiencesSearchPlaceSelectList from './ExperiencesSearchPlaceSelectList'
import { rem } from 'polished'
import { take } from '../../../lib/array/arrayUtils'
import Caption from 'LuxKit/Typography/Caption'
import { bumpExperienceRecentSearch, deleteExperienceRecentSearch } from 'components/Experiences/hooks/useExperienceRecentSearches'
import ExperienceRecentSearchSuggestions from './ExperienceRecentSearchSuggestions'

const SearchMenuListItemContainer = styled.div`
  column-gap: ${rem(40)};

  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: 100%;
`

const RecentSearchSuggestionsContainer = styled(VerticalSpacer)`
  margin-bottom:  ${rem(28)};
`

const StyledCaption = styled(Caption)`
  padding: 0;
`

const NUMBER_OF_POPULAR_PLACES_DISPLAYED = 9
const RECENT_SEARCH_SUBTITLE_OPTIONS = {
  includeRooms: false,
}

interface Props {
  className?: string
  'data-testid'?: string
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  value?: App.SearchItem
  required?: boolean
  label: string
  placeholder: string
  localInputRef: React.RefObject<HTMLInputElement>
  searchPhrase: string
  onSearchPhraseChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleOpen: (e?: unknown) => void
  onClearClick: () => void
  showPlaceholder: boolean
  recentSearches: Array<App.FullRecentSearch>
  selectedDropdown: number | undefined
  onRecentSearchDelete: (item: App.FullRecentSearch) => void
  onRecentSearchClick: (item: App.FullRecentSearch) => void
  placeholderTitle?: string
  placeholdersSearches: Array<App.SearchItem>
  onLocationSelect: (
    searchItem?: App.SearchItem,
    mouseEvent?: React.MouseEvent<HTMLElement>,
  ) => void
  handleResultsChange: (results: Array<App.SearchItem>, searchPhrase: string, shouldTrack?: boolean, loading?: boolean) => void
  searchVertical?: App.SearchPlaceVertical
  temporaryTypeaheadId?: string
  temporarySearchSubmitRef?: React.MutableRefObject<App.TypeaheadSubmitRefData | null>
}

function ExperiencesSearchPlaceSelectInline(props: Props) {
  const {
    className,
    onKeyDown,
    value,
    required,
    label,
    placeholder,
    localInputRef,
    searchPhrase,
    onSearchPhraseChange,
    handleOpen,
    onClearClick,
    showPlaceholder,
    recentSearches,
    selectedDropdown,
    onRecentSearchDelete,
    onRecentSearchClick,
    placeholderTitle,
    placeholdersSearches,
    onLocationSelect,
    handleResultsChange,
    searchVertical,
    temporaryTypeaheadId,
    temporarySearchSubmitRef,
    'data-testid': dataTestId,
  } = props

  const placeHolderSearchItems = take(placeholdersSearches, NUMBER_OF_POPULAR_PLACES_DISPLAYED)

  const handleRecentSearchDelete = useCallback((recentSearch: App.FullRecentSearch) => {
    deleteExperienceRecentSearch(recentSearch)
    onRecentSearchDelete(recentSearch)
  }, [onRecentSearchDelete])

  const handleRecentSearchClick = useCallback((recentSearch: App.FullRecentSearch) => {
    bumpExperienceRecentSearch(recentSearch)
    onRecentSearchClick(recentSearch)
  }, [onRecentSearchClick])

  return (
    <VerticalSpacer gap={16} className={className} onKeyDown={onKeyDown}>
      <HiddenInput
        required={required}
        value={value?.format.mainText}
      />
      <SearchFormTextField
        data-testid={dataTestId}
        label={label}
        placeholder={placeholder}
        ref={localInputRef}
        value={searchPhrase}
        displayValue={value?.format.mainText ?? ''}
        onChange={onSearchPhraseChange}
        onFocus={handleOpen}
        onClear={onClearClick}
      />
      {showPlaceholder && <>
        {!!recentSearches.length && <RecentSearchSuggestionsContainer gap={16}>
          <ExperienceRecentSearchSuggestions
            recentSearches={recentSearches}
            onDelete={handleRecentSearchDelete}
            selectedDropdown={selectedDropdown}
            iconBackground
            onRecentSearchClick={handleRecentSearchClick}
            horizontal
            subtitleOptions={RECENT_SEARCH_SUBTITLE_OPTIONS}
          />
        </RecentSearchSuggestionsContainer>}
        <VerticalSpacer gap={12}>
          {placeholderTitle && <StyledCaption variant="large" weight="bold">{placeholderTitle}</StyledCaption>}
          <SearchMenuListItemContainer className="horizontal">
            {placeHolderSearchItems.map((item, index) => <SearchMenuListItem
              key={item.value}
              searchItem={item}
              onClick={onLocationSelect}
              selected={index + recentSearches.length === selectedDropdown}
              imageId={item.imageId}
              searchVertical={searchVertical}
              useImage
            />)}
          </SearchMenuListItemContainer>
        </VerticalSpacer>
      </>}
      {!showPlaceholder && temporaryTypeaheadId &&
        <ExperiencesSearchPlaceSelectList
          searchPhrase={searchPhrase}
          typeaheadId={temporaryTypeaheadId}
          selectedDropdown={selectedDropdown}
          onChange={onLocationSelect}
          onResultsChange={handleResultsChange}
          temporarySearchSubmitRef={temporarySearchSubmitRef}
        />}
    </VerticalSpacer>
  )
}

export default ExperiencesSearchPlaceSelectInline
